import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTabsComponent, QaiTabDirective } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { PracticeTabComponent } from './practice-tab/practice-tab.component';
import { CertificationTabComponent } from './certification-tab/certification-tab.component';
import { CommonModule } from '@angular/common';
import { IUserAssignmentRequest } from 'src/app/interfaces/iuser-assignment-request';
import { IUserAssignmentResponse } from 'src/app/interfaces/iuser-assignment-response';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { AppConstants } from 'src/app/app-constants';
import { IUserAssignmentMetricsResponse } from 'src/app/interfaces/iuser-assignment-metrics-response';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-user-assignments',
  templateUrl: './user-assignments.component.html',
  styleUrls: ['./user-assignments.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslocoModule, QaiTabsComponent, QaiTabDirective, DetailsSectionComponent, PracticeTabComponent, CertificationTabComponent],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
})
export class UserAssignmentsComponent implements OnInit {
  isLoading: boolean = false;
  isUserAssignmentsLoading: boolean = false;
  isUserMetricsLoading: boolean = false;
  userAssignmentMetrics: IUserAssignmentMetricsResponse = {
    percentCertifiedFirstTry: 0,
    avgCertificationScore: 0,
    totalTimeCertifyingSeconds: 0,
    totalCertificationAssignments: 0,
    totalSessions: 0,
    averageSessionsPerAssignment: 0,
    assignmentsEngagedWith: 0,
    averageImprovementPercent: 0
  };

  private readonly TAB_IDS = {
    PRACTICE: 'practice',
    CERTIFICATION: 'certification'
  } as const;

  isTranslationLoaded = false;
  activeTab = this.TAB_IDS.PRACTICE;

  // Assignment data
  practiceAssignments: IUserAssignmentResponse[] = [];
  certificationAssignments: IUserAssignmentResponse[] = [];

  @Output() tabChange = new EventEmitter<string>();

  constructor(
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeActiveTab();
        this.getUserAssignments(this.TAB_IDS.PRACTICE);
        this.getUserAssignments(this.TAB_IDS.CERTIFICATION);
        this.getUserAssignmentMetrics();
      })
      .catch((error) => {
        this.isTranslationLoaded = false;
      });
  }

  private initializeActiveTab(): void {
    this.activeTab = this.TAB_IDS.PRACTICE;
  }

  private getAssignmentUuidFromQueryParams(): string | null {
    return this.route.snapshot.queryParams['assignmentUuid'] || null;
  }

  onTabChange(event: any): void {
    const tabId = typeof event === 'string' ? event : event?.detail?.tabId || event?.tabId || event?.id || '';
    if (tabId) {
      this.activeTab = tabId;
      this.tabChange.emit(this.activeTab);
      console.log('Active tab changed to:', tabId);
      this.getUserAssignments(tabId);
      this.getUserAssignmentMetrics();
    }
  }

  private buildUserAssignmentPayload(tabType?: string): IUserAssignmentRequest | null {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    const userUuid = this.route.snapshot.paramMap.get('id');

    if (!accountUuid || !userUuid) {
      console.warn('Missing required parameters for user assignment payload', {
        accountUuid,
        userUuid,
      });
      return null;
    }

    const targetTab = tabType || this.activeTab;
    let assignmentType: string = '';
    if (targetTab === this.TAB_IDS.PRACTICE) {
      assignmentType = AppConstants.TYPE_PRACTICE;
    } else if (targetTab === this.TAB_IDS.CERTIFICATION) {
      assignmentType = AppConstants.TYPE_CERTIFICATION;
    }

    const assignmentUuid = this.getAssignmentUuidFromQueryParams();

    return {
      accountUuid: accountUuid,
      userUuid: userUuid,
      type: assignmentType,
      moduleAssignmentUuids: assignmentUuid ? [assignmentUuid] : null,
    };
  }

  private getUserAssignments(tabType?: string): void {
    const targetTab = tabType || this.activeTab;
    const payload = this.buildUserAssignmentPayload(targetTab);

    if (!payload) {
      return;
    }

    this.isUserAssignmentsLoading = true;
    this.updateIsLoading();
    this.reportingDashboardService.getUserAssignments(payload, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (assignments) => {
        this.isUserAssignmentsLoading = false;
        this.updateIsLoading();
        if (targetTab === this.TAB_IDS.PRACTICE) {
          this.practiceAssignments = assignments;
        } else if (targetTab === this.TAB_IDS.CERTIFICATION) {
          this.certificationAssignments = assignments;
        }
      },
      error: (error) => {
        console.error('Error loading user assignments:', error);
        this.isUserAssignmentsLoading = false;
        this.updateIsLoading();
      },
    });
  }

  private getUserAssignmentMetrics(): void {
    const payload = this.buildUserAssignmentPayload();

    if (!payload) {
      return;
    }

    this.isUserMetricsLoading = true;
    this.updateIsLoading();
    this.reportingDashboardService.getUserAssignmentMetrics(payload, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (metrics: IUserAssignmentMetricsResponse) => {
        this.isUserMetricsLoading = false;
        this.updateIsLoading();
        this.userAssignmentMetrics = metrics;
        console.log('userAssignmentMetrics', this.userAssignmentMetrics);
      },
      error: (error) => {
        console.error('Error loading user assignment metrics:', error);
        this.isUserMetricsLoading = false;
        this.updateIsLoading();
      },
    });
  }

  private updateIsLoading(): void {
    this.isLoading = this.isUserAssignmentsLoading && this.isUserMetricsLoading;
  }
}
